import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Range, getTrackBackground } from 'react-range';
import { Rnd } from 'react-rnd';
import { trimCropRecording, getProcessingStatus } from '../../utils/recordingApi';
import { PlatformConfigs } from '../../utils/PlatformConfigs';
import { formatTime } from '../../utils/helper';

// Calculate the initial crop dimensions based on the selected platform
function calculateInitialCrop(selectedPlatform, displayedWidth, displayedHeight) {
  if (!displayedWidth || !displayedHeight) return { x: 0, y: 0, width: 0, height: 0 };

  const config = PlatformConfigs.find((c) => c.value === selectedPlatform);
  if (!config || selectedPlatform === 'CUSTOM') {
    const cropWidth = displayedWidth * 0.6;
    const cropHeight = displayedHeight * 0.6;
    return {
      x: (displayedWidth - cropWidth) / 2,
      y: (displayedHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    };
  }

  const { aspectRatio, maxWidth, maxHeight } = config;
  let cropWidth = displayedWidth * 0.8;
  let cropHeight = cropWidth / aspectRatio;

  if (cropHeight > displayedHeight * 0.8) {
    cropHeight = displayedHeight * 0.8;
    cropWidth = cropHeight * aspectRatio;
  }

  cropWidth = Math.min(cropWidth, maxWidth, displayedWidth);
  cropHeight = Math.min(cropHeight, maxHeight, displayedHeight);

  return {
    x: (displayedWidth - cropWidth) / 2,
    y: (displayedHeight - cropHeight) / 2,
    width: cropWidth,
    height: cropHeight,
  };
}

const RecordingTrimCrop = ({ recording, onClose }) => {
  const videoRef = useRef(null);
  
  // State variables - simplified like VideoTrimCrop
  const [trimRange, setTrimRange] = useState([0, recording.duration || 0]);
  const [playerDisplayDimensions, setPlayerDisplayDimensions] = useState({ width: 0, height: 0 });
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [selectedPlatform, setSelectedPlatform] = useState('CUSTOM');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [resultVideoUrl, setResultVideoUrl] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  // Update display dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      if (videoRef.current) {
        const rect = videoRef.current.getBoundingClientRect();
        setPlayerDisplayDimensions({ width: rect.width, height: rect.height });
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial call
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set initial crop values when display dimensions or platform change
  useEffect(() => {
    const { width, height } = playerDisplayDimensions;
    if (width && height) {
      const initialCrop = calculateInitialCrop(selectedPlatform, width, height);
      setCrop(initialCrop);
    }
  }, [playerDisplayDimensions, selectedPlatform]);

  const handleTrimCrop = async () => {
    if (!recording.id) {
      toast.error('Recording ID not found');
      return;
    }

    // Check if crop area is properly initialized
    if (crop.width <= 0 || crop.height <= 0) {
      setError('Please select a crop area before proceeding.');
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      setResultVideoUrl(null);
      setProgress(0);
      
      const videoElement = videoRef.current;
      if (!videoElement) {
        setError('Video element not found.');
        setIsProcessing(false);
        return;
      }

      const duration = videoElement.duration || recording.duration || 0;
      const [startSeconds, endSeconds] = trimRange;
      
      if (startSeconds >= endSeconds) {
        toast.error('Start time must be less than end time.');
        setIsProcessing(false);
        return;
      }

      if (endSeconds > duration) {
        toast.error('End time exceeds video duration.');
        setIsProcessing(false);
        return;
      }

      const actualWidth = videoElement.videoWidth;
      const actualHeight = videoElement.videoHeight;
      if (!actualWidth || !actualHeight) {
        setError('Video dimensions not available. Please wait for the video to load completely.');
        setIsProcessing(false);
        return;
      }

      const { width: displayedWidth, height: displayedHeight } = playerDisplayDimensions;
      if (!displayedWidth || !displayedHeight) {
        setError('Video display dimensions not ready.');
        setIsProcessing(false);
        return;
      }

      const scaleX = actualWidth / displayedWidth;
      const scaleY = actualHeight / displayedHeight;
      const mappedX = Math.round(crop.x * scaleX);
      const mappedY = Math.round(crop.y * scaleY);
      let mappedWidth = Math.round(crop.width * scaleX);
      let mappedHeight = Math.round(crop.height * scaleY);

      const platformConfig = PlatformConfigs.find((c) => c.value === selectedPlatform);
      if (platformConfig && selectedPlatform !== 'CUSTOM') {
        mappedWidth = Math.min(mappedWidth, platformConfig.maxWidth);
        mappedHeight = Math.min(mappedHeight, platformConfig.maxHeight);
      }

      // Validate crop dimensions are positive
      if (mappedWidth <= 0 || mappedHeight <= 0) {
        setError('Crop dimensions must be positive. Please adjust the crop area.');
        setIsProcessing(false);
        return;
      }

      // Additional validation for crop bounds
      if (mappedX < 0 || mappedY < 0 || (mappedX + mappedWidth) > actualWidth || (mappedY + mappedHeight) > actualHeight) {
        setError('Crop area is outside video bounds. Please adjust the crop area.');
        setIsProcessing(false);
        return;
      }

      // Create DTO matching backend TrimAndCropDto structure
      const trimAndCropDto = {
        startSeconds,
        endSeconds,
        cropDto: {
          x: mappedX,
          y: mappedY,
          width: mappedWidth,
          height: mappedHeight,
          socialMediaPlatform: selectedPlatform !== 'CUSTOM' ? selectedPlatform : 'CUSTOM',
        },
      };

      console.log('🔍 Sending TrimAndCropDto:', trimAndCropDto);

      const response = await trimCropRecording(recording.id, trimAndCropDto);
      console.log('🔍 Trim/crop response:', response);
      
      const trackingId = response.data || response;
      console.log('🔍 Received tracking ID:', trackingId);
      
      if (trackingId) {
        toast.success('Trim and crop processing started!');
        monitorProcessingStatus(trackingId);
      } else {
        throw new Error('No tracking ID received from server');
      }
    } catch (err) {
      console.error('🔍 Trim/crop error:', err);
      console.error('🔍 Error response:', err.response?.data);
      
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
      setError(`Error initiating trim and crop: ${errorMessage}`);
      setIsProcessing(false);
    }
  };

  const monitorProcessingStatus = async (trackingId) => {
    try {
      console.log('🔍 Monitoring processing status for tracking ID:', trackingId);
      
      const processingInfo = await getProcessingStatus(trackingId);
      console.log('🔍 Processing status data:', processingInfo);

      // Check for different possible status field names
      const status = processingInfo.videoProcessingStatus || processingInfo.status;
      const progress = processingInfo.videoProcessingProgress || processingInfo.progress;
      const errorMessage = processingInfo.videoProcessingErrorMessage || processingInfo.errorMessage;
      const preSignedUrl = processingInfo.preSignedUrl || processingInfo.resultUrl;

      console.log('🔍 Parsed status fields:', {
        status,
        progress,
        errorMessage,
        preSignedUrl
      });

      if (status === 'PROCESSED' || status === 'COMPLETED') {
        toast.success('Video processing completed successfully!');
        setIsProcessing(false);
        setProgress(100);
        setResultVideoUrl(preSignedUrl);
      } else if (status === 'FAILED') {
        toast.error(`Video processing failed: ${errorMessage || 'Unknown error'}`);
        setIsProcessing(false);
      } else if (status === 'IN_PROGRESS' || status === 'PROCESSING') {
        setProgress(progress || 0);
        // Continue monitoring
        setTimeout(() => monitorProcessingStatus(trackingId), 5000);
      } else {
        console.log('🔍 Unknown status, continuing to monitor:', status);
        // Continue monitoring for unknown statuses
        setTimeout(() => monitorProcessingStatus(trackingId), 5000);
      }
    } catch (err) {
      console.error('🔍 Error monitoring processing status:', err);
      console.error('🔍 Error details:', err.response?.data);
      toast.error('Failed to check processing status. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!resultVideoUrl) {
      toast.error('Processed video URL is not available.');
      return;
    }

    try {
      setIsDownloading(true);
      setDownloadProgress(0);

      const response = await fetch(resultVideoUrl);
      if (!response.ok) {
        throw new Error('Failed to download the processed video.');
      }

      const contentLength = response.headers.get('Content-Length');
      if (!contentLength) {
        throw new Error('Content-Length header is missing.');
      }

      const total = parseInt(contentLength, 10);
      let loaded = 0;
      const reader = response.body.getReader();
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.length;
        const progress = Math.floor((loaded / total) * 100);
        setDownloadProgress(progress);
      }

      const blob = new Blob(chunks, { type: 'video/mp4' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `processed_recording_${recording.id}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setIsDownloading(false);
      toast.success('Processed video downloaded successfully.');
    } catch (error) {
      setError('Failed to download the processed video. Please try again.');
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4 text-[#010101] bg-gradient-to-br from-white to-[#00a693]/5 rounded-2xl shadow-2xl overflow-hidden border border-[#00a693]/10">
      {error && (
        <div className="text-[#BD2026] text-center mb-4 text-sm bg-red-50/80 backdrop-blur-sm border border-[#BD2026]/20 rounded-xl p-4 shadow-lg">
          {error}
        </div>
      )}

      {!resultVideoUrl ? (
        <>
          <div className="flex-1 p-6 overflow-y-auto md:p-4">
            <div className="relative w-full pt-[56.25%] bg-gray-100 rounded-md overflow-hidden shadow-lg md:pt-[75%]">
              <video
                ref={videoRef}
                src={recording.presignedUrl}
                controls
                className="absolute top-0 left-0 w-full h-full rounded-md bg-gray-50"
                onLoadedMetadata={() => {
                  if (videoRef.current && videoRef.current.duration) {
                    const totalDuration = videoRef.current.duration;
                    setTrimRange([0, Math.floor(totalDuration)]);
                  }
                }}
                poster={recording.thumbnails?.[0]?.url}
                aria-label="Video player for trimming and cropping"
              />
              {crop.width > 0 && crop.height > 0 && (
                <Rnd
                  size={{ width: crop.width, height: crop.height }}
                  position={{ x: crop.x, y: crop.y }}
                  bounds="parent"
                  minWidth={100}
                  minHeight={100}
                  lockAspectRatio={selectedPlatform !== 'CUSTOM'}
                  aspectRatio={
                    selectedPlatform !== 'CUSTOM'
                      ? PlatformConfigs.find((c) => c.value === selectedPlatform)?.aspectRatio
                      : undefined
                  }
                  onDragStop={(e, data) => {
                    setCrop((prev) => ({ ...prev, x: data.x, y: data.y }));
                  }}
                  onResizeStop={(e, direction, ref, delta, position) => {
                    setCrop({
                      x: position.x,
                      y: position.y,
                      width: parseFloat(ref.style.width),
                      height: parseFloat(ref.style.height),
                    });
                  }}
                  enableResizing={{
                    top: true,
                    right: true,
                    bottom: true,
                    left: true,
                    topRight: true,
                    bottomRight: true,
                    bottomLeft: true,
                    topLeft: true,
                  }}
                  style={{ 
                    zIndex: 10,
                    border: '2px solid #BD2026',
                    backgroundColor: 'rgba(189, 32, 38, 0.1)',
                    boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
                    cursor: 'move'
                  }}
                  aria-label="Crop selection area"
                />
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-text-primary mb-1">
                Select Crop Dimension
              </label>
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                aria-label="Select crop dimension"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary"
              >
                <option value="CUSTOM">Custom (Free Crop)</option>
                {PlatformConfigs.map((config) => (
                  <option key={config.value} value={config.value}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-text-primary mb-1">Trim Video</label>
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Start: {formatTime(trimRange[0])}</span>
                <span>End: {formatTime(trimRange[1])}</span>
              </div>
              {recording.duration && recording.duration > 0 ? (
                <Range
                  step={1}
                  min={0}
                  max={recording.duration}
                  values={trimRange}
                  onChange={(values) => setTrimRange(values)}
                  renderTrack={({ props, children }) => {
                    const { key, ...trackProps } = props;
                    return (
                      <div
                        key={key}
                        {...trackProps}
                        style={{
                          ...trackProps.style,
                          height: '6px',
                          width: '100%',
                          background: getTrackBackground({
                            values: trimRange,
                            colors: ['#e5e7eb', '#BD2026', '#e5e7eb'],
                            min: 0,
                            max: recording.duration,
                          }),
                        }}
                        aria-label="Trim video range"
                      >
                        {children}
                      </div>
                    );
                  }}
                  renderThumb={({ props, isDragged }) => {
                    const { key, ...thumbProps } = props;
                    return (
                      <div
                        key={key}
                        {...thumbProps}
                        style={{
                          ...thumbProps.style,
                          height: '24px',
                          width: '24px',
                          borderRadius: '12px',
                          backgroundColor: '#fff',
                          border: '2px solid #BD2026',
                          boxShadow: '0 0 0 2px rgba(0,0,0,0.5)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                        aria-label={isDragged ? 'Dragging thumb' : 'Trim slider thumb'}
                      />
                    );
                  }}
                />
              ) : (
                <div style={{ opacity: 0.5, pointerEvents: 'none' }}>
                  <Range
                    step={1}
                    min={0}
                    max={1}
                    values={[0, 1]}
                    onChange={() => {}}
                    renderTrack={({ props, children }) => {
                      const { key, ...trackProps } = props;
                      return (
                        <div
                          key={key}
                          {...trackProps}
                          style={{
                            ...trackProps.style,
                            height: '6px',
                            width: '100%',
                            background: '#f3f4f6',
                          }}
                        >
                          {children}
                        </div>
                      );
                    }}
                    renderThumb={({ props }) => {
                      const { key, ...thumbProps } = props;
                      return (
                        <div
                          key={key}
                          {...thumbProps}
                          style={{
                            ...thumbProps.style,
                            height: '24px',
                            width: '24px',
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            border: '2px solid #d1d5db',
                            boxShadow: '0 0 0 2px rgba(0,0,0,0.1)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        />
                      );
                    }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Loading video duration...
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 p-6 bg-gray-100 border-t border-gray-200 flex justify-between items-center md:p-4 md:flex-col md:gap-2">
            <button 
              onClick={onClose} 
              className="flex items-center justify-center px-6 py-3 bg-gray-100 text-[#010101] border-none rounded-xl text-sm font-medium cursor-pointer transition-all duration-300 hover:bg-gray-200 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:transform-none"
            >
              Cancel
            </button>
            <button 
              onClick={handleTrimCrop} 
              disabled={isProcessing}
              className="flex items-center justify-center px-6 py-3 bg-[#00a693] text-white border-none rounded-xl text-sm font-semibold cursor-pointer transition-all duration-300 hover:bg-[#008a7a] hover:-translate-y-1 hover:shadow-lg hover:shadow-[#00a693]/25 active:translate-y-0 active:shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:transform-none gap-2 tracking-wide"
            >
              {isProcessing ? (
                <>
                  Processing... 
                  <div className="inline-block w-4 h-4 border-2 border-gray-200 border-t-white rounded-full animate-spin ml-1" />
                </>
              ) : (
                'Trim & Crop'
              )}
            </button>

            {isProcessing && (
              <div className="mt-4 w-full">
                <div className="flex items-center gap-2" aria-label={`Processing progress: ${progress}%`}>
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-secondary rounded-full transition-all duration-300"
                      style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                    />
                  </div>
                  <span className="text-xs text-text-secondary min-w-[40px] text-right">
                    {progress >= 0 ? `${progress}%` : 'Processing...'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="flex-1 p-6 overflow-y-auto md:p-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold mb-2">Video Processing Complete!</h3>
              <p className="text-gray-600">Your trimmed and cropped recording is ready.</p>
            </div>
            <div className="relative w-full pt-[56.25%] bg-gray-100 rounded-md overflow-hidden shadow-lg md:pt-[75%]">
              <video
                src={resultVideoUrl}
                controls
                className="absolute top-0 left-0 w-full h-full rounded-md bg-gray-50"
                aria-label="Processed video preview"
              />
            </div>
          </div>

          <div className="flex-shrink-0 p-6 bg-gray-100 border-t border-gray-200 flex justify-between items-center md:p-4 md:flex-col md:gap-2">
            <div className="text-center mb-4">
              <h4 className="text-base font-medium mb-1">Your Video is Ready!</h4>
              <p className="text-sm text-gray-600 mb-4">Download now or close to return to recordings.</p>
            </div>
            
            <button 
              onClick={handleDownload} 
              disabled={isDownloading}
              className="flex items-center justify-center px-6 py-3 bg-[#00a693] text-white border-none rounded-xl text-sm font-semibold cursor-pointer transition-all duration-300 hover:bg-[#008a7a] hover:-translate-y-1 hover:shadow-lg hover:shadow-[#00a693]/25 active:translate-y-0 active:shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:transform-none gap-2 tracking-wide"
            >
              {isDownloading ? (
                <>
                  <div className="inline-block w-4 h-4 border-2 border-gray-200 border-t-white rounded-full animate-spin ml-1" />
                  Downloading... {downloadProgress}%
                </>
              ) : (
                <>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download
                </>
              )}
            </button>
            
            <button 
              onClick={onClose}
              className="flex items-center justify-center px-6 py-3 bg-gray-100 text-[#010101] border-none rounded-xl text-sm font-medium cursor-pointer transition-all duration-300 hover:bg-gray-200 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:transform-none"
            >
              Close
            </button>

            {isDownloading && (
              <div className="mt-4 w-full">
                <div className="flex items-center gap-2" aria-label={`Download progress: ${downloadProgress}%`}>
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent-teal rounded-full transition-all duration-300"
                      style={{ width: `${Math.max(0, Math.min(100, downloadProgress))}%` }}
                    />
                  </div>
                  <span className="text-xs text-text-secondary min-w-[40px] text-right">
                    {downloadProgress}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

RecordingTrimCrop.propTypes = {
  recording: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RecordingTrimCrop;